import React from "react";
import { LoadPhase, SkeletonCard } from "../../../components/ui";

/**
 * The card one of the dashboard's feeds sits in: a heading, and the table under
 * it.
 *
 * Shared by both feeds so they are the same card rather than two cards that
 * happen to look alike. What is inside is each table's business — including its
 * empty state, which belongs in the table body so the column headings stay put
 * and the reader can still see what the list would have held.
 */
interface ActivityCardProps {
  title: string;
  /**
   * Where the rows are. While they are on their way the body says nothing, and
   * only a load slow enough to notice shimmers — as one block the size of the
   * table. See `useLoadPhase`.
   */
  phase?: LoadPhase;
  /** Rows already in hand are left alone while the next answer is fetched: a
   *  table with something to show goes on showing it. */
  hasRows?: boolean;
  children: React.ReactNode;
}

/**
 * The whole card's footprint — its heading, the column headings and five rows —
 * so the block that stands in for it is the size of the card that lands in its
 * place.
 */
const FOOTPRINT = "min-h-[420px] 3xl:min-h-[470px]";

const ActivityCard: React.FC<ActivityCardProps> = ({
  title,
  phase = "ready",
  hasRows = false,
  children,
}) =>
  // The card is not drawn at all until its rows arrive — a heading over a
  // shimmering block reads as a table that failed rather than one on its way,
  // and it is the one part of the card that would land twice. So the whole
  // thing shimmers as one block, the way the tiles above it do.
  //
  // Hidden from screen readers with the rest of the placeholders; the page
  // says it is loading in words.
  phase !== "ready" && !hasRows ? (
    <SkeletonCard phase={phase} className={FOOTPRINT} />
  ) : (
    <section className="overflow-hidden rounded-md border border-line bg-white">
      <div className="p-4 sm:p-6 sm:pb-4">
        <h2 className="text-[16px] sm:text-[18px] 3xl:text-[20px] font-medium text-[#111C2D]">
          {title}
        </h2>
      </div>

      {children}
    </section>
  );

export default ActivityCard;
